import React, { useRef, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";

import { CardTitle, ConsoleCard } from "./consoleParts";
import { ImportErrors } from "./ImportErrors";
import { ImportReport } from "./ImportReport";
import { readSheet } from "./sheetFiles";
import { failureMessage, isRefusedReport } from "./uploadOutcome";
import { importWarmUpSheet } from "./warmUpSetsAdminApi";

const CSV_ACCEPT = ".csv,text/csv";
const IMAGE_ACCEPT = "image/png,image/jpeg,image/gif,image/webp,image/svg+xml,.png,.jpg,.jpeg,.gif,.webp,.svg";

const CSV_INPUTS = [
  { name: "sets", label: "Sets tab (CSV)", hint: "The Sets tab of the warm-up sheet, downloaded as CSV." },
  { name: "questions", label: "Questions tab (CSV)", hint: "The Questions tab of the warm-up sheet, downloaded as CSV." },
  {
    name: "misconceptions",
    label: "Misconceptions tab (CSV)",
    hint: "The Misconceptions tab of the warm-up sheet, downloaded as CSV.",
  },
];

const IMAGES_HELP =
  "The Questions tab's Image File cell may hold a file name or a link. Links: Google Drive files shared with 'Anyone with the link', Google Cloud Storage or the OKO image CDN; they are downloaded on every Check and Import. Upload files only for the cells that hold a file name. Leave this empty to keep the images already stored for those cells; if you add images, add every file the Questions tab names.";
const ACTIVATE_LABEL =
  "Turn on every set in these files (except image sets missing an image). This also turns on sets that were turned off on this page.";

const NO_CSVS = { sets: null, questions: null, misconceptions: null };

const CHECK = "check";
const IMPORT = "import";

const FileProblem = ({ problem }) =>
  problem ? (
    <div className="text-danger small mt-1" role="alert">
      {problem}
    </div>
  ) : null;

export const UploadSheetCard = ({ onImported }) => {
  const [csvs, setCsvs] = useState(NO_CSVS);
  const [images, setImages] = useState([]);
  const [activate, setActivate] = useState(false);
  const [checkPassed, setCheckPassed] = useState(false);
  const [report, setReport] = useState(null);
  const [refusedErrors, setRefusedErrors] = useState(null);
  const [failure, setFailure] = useState(null);
  const [running, setRunning] = useState(null);
  const [problems, setProblems] = useState({});
  // Bumped on every change of the files or the checkbox, so a Check that
  // answers after a change cannot vouch for a selection it never saw.
  const selectionVersion = useRef(0);

  const allCsvsChosen = Object.values(csvs).every(Boolean);

  const forgetOutcome = () => {
    setProblems({});
    setCheckPassed(false);
    setReport(null);
    setRefusedErrors(null);
    setFailure(null);
  };

  const changeSelection = () => {
    selectionVersion.current += 1;
    forgetOutcome();
  };

  const chooseCsv = (name) => (event) => {
    const file = event.target.files[0] || null;
    setCsvs((prev) => ({ ...prev, [name]: file }));
    changeSelection();
  };

  const chooseImages = (event) => {
    setImages(Array.from(event.target.files));
    changeSelection();
  };

  const toggleActivate = (event) => {
    setActivate(event.target.checked);
    changeSelection();
  };

  const send = (kind) => async () => {
    const dryRun = kind === CHECK;
    const sentVersion = selectionVersion.current;
    // An Import's answer is always shown: it reports what was written.
    const isStale = () => dryRun && selectionVersion.current !== sentVersion;
    forgetOutcome();
    setRunning(kind);
    try {
      const sheet = await readSheet(csvs, images);
      if (isStale()) return;
      if (sheet.problems) {
        setProblems(sheet.problems);
        return;
      }
      const answer = await importWarmUpSheet({ ...sheet.body, activate, dry_run: dryRun });
      if (isStale()) return;
      setReport(answer);
      setCheckPassed(dryRun && answer.ok === true);
      if (!dryRun) onImported?.();
    } catch (error) {
      if (isStale()) return;
      if (isRefusedReport(error)) setRefusedErrors(error.errors);
      else setFailure(failureMessage(error));
    } finally {
      setRunning(null);
    }
  };

  return (
    <ConsoleCard>
      <CardTitle>Upload sheet</CardTitle>
      {CSV_INPUTS.map(({ name, label, hint }) => (
        <Form.Group className="mb-3" controlId={`warm-up-${name}-csv`} key={name}>
          <Form.Label>{label}</Form.Label>
          <Form.Control
            type="file"
            required
            accept={CSV_ACCEPT}
            aria-describedby={`warm-up-${name}-csv-hint`}
            onChange={chooseCsv(name)}
          />
          <Form.Text id={`warm-up-${name}-csv-hint`} className="text-muted">
            {hint}
          </Form.Text>
          <FileProblem problem={problems[name]} />
        </Form.Group>
      ))}
      <Form.Group className="mb-3" controlId="warm-up-images">
        <Form.Label>Question images</Form.Label>
        <Form.Control
          type="file"
          multiple
          accept={IMAGE_ACCEPT}
          aria-describedby="warm-up-images-help"
          onChange={chooseImages}
        />
        <Form.Text id="warm-up-images-help" className="text-muted">
          {IMAGES_HELP}
        </Form.Text>
        <FileProblem problem={problems.images} />
      </Form.Group>
      <Form.Group className="mb-4" controlId="warm-up-activate">
        <Form.Check type="checkbox" label={ACTIVATE_LABEL} checked={activate} onChange={toggleActivate} />
      </Form.Group>
      <Button type="button" disabled={!allCsvsChosen || running !== null} onClick={send(CHECK)}>
        {running === CHECK ? "Checking…" : "Check files"}
      </Button>{" "}
      <Button type="button" disabled={!checkPassed || running !== null} onClick={send(IMPORT)}>
        {running === IMPORT ? "Importing…" : "Import"}
      </Button>
      {failure && (
        <Alert variant="danger" className="mt-4">
          {failure}
        </Alert>
      )}
      {report && <ImportReport report={report} />}
      {refusedErrors && <ImportErrors errors={refusedErrors} />}
    </ConsoleCard>
  );
};
